// apps/web/src/widgets/static-note/markdown-allowlist.tsx
//
// Phase 6 — static-note markdown allowlist (UI-SPEC §"Markdown Allowlist").
//
// Two layered defenses are exported:
//   1. ALLOWED_ELEMENTS — strict allowlist of HTML tag names that react-markdown
//      will keep in the output. Tags outside this list are pruned via the
//      `allowedElements` prop (drops <h1>, <h2>, <img>, <table>, etc.).
//   2. COMPONENTS_ALLOWLIST — per-tag renderer overrides for the allowed tags
//      (custom classNames, target=_blank on <a>, etc.).
//   3. safeUrlTransform — rejects javascript:/data:/file: schemes.
//
// react-markdown v9 disables raw HTML pass-through by default (no rehype-raw),
// so <script>, <iframe>, etc. are already dropped at parse time. The
// `allowedElements` prop adds defense-in-depth at the AST-prune stage.
//
// NEVER install rehype-raw — it re-enables raw HTML pass-through. Phase 6 forbids.
import type { Components } from "react-markdown";

// AST element names that react-markdown is permitted to emit. Anything outside
// this list is pruned via the `allowedElements` prop on <ReactMarkdown>.
//
// Excluded by design (UI-SPEC §"Markdown Allowlist"):
//   - h1, h2: reserved for framework chrome
//   - img: drop entirely (no inline images in static-note in Phase 6)
//   - table, thead, tbody, tr, th, td: drop GFM tables
//   - blockquote, hr: not in the allowlist
//   - script, iframe, style: blocked at parse time already, listed here as
//     belt-and-braces
export const ALLOWED_ELEMENTS: ReadonlyArray<string> = [
  "p",
  "strong",
  "em",
  "del",
  "ul",
  "ol",
  "li",
  "a",
  "code",
  "pre",
  "br",
  "h3",
  "h4",
  "h5",
  "h6",
];

export const COMPONENTS_ALLOWLIST: Components = {
  p: (props) => <p className="text-sm leading-6">{props.children}</p>,
  strong: (props) => <strong className="font-semibold">{props.children}</strong>,
  em: (props) => <em className="italic">{props.children}</em>,
  ul: (props) => <ul className="list-disc pl-5">{props.children}</ul>,
  ol: (props) => <ol className="list-decimal pl-5">{props.children}</ol>,
  li: (props) => <li className="text-sm leading-6">{props.children}</li>,
  a: ({ href, children }) => (
    <a href={href} rel="noopener noreferrer" target="_blank" className="underline">
      {children}
    </a>
  ),
  code: (props) => (
    <code className="font-mono text-sm bg-muted px-1 py-1 rounded">{props.children}</code>
  ),
  pre: (props) => (
    <pre className="font-mono text-sm bg-muted p-3 rounded-md overflow-x-auto">
      {props.children}
    </pre>
  ),
  br: () => <br />,
  h3: (props) => <h3 className="text-base font-semibold leading-tight">{props.children}</h3>,
  h4: (props) => <h4 className="text-sm font-semibold leading-5">{props.children}</h4>,
  h5: (props) => <h5 className="text-sm font-semibold leading-5">{props.children}</h5>,
  h6: (props) => <h6 className="text-sm font-semibold leading-5">{props.children}</h6>,
  // h1, h2 INTENTIONALLY OMITTED — reserved for framework chrome per UI-SPEC.
  // img, table, iframe, script, blockquote — INTENTIONALLY OMITTED — drop.
};

/**
 * URL transform — only allow http/https/mailto schemes. Returns undefined to drop.
 * react-markdown calls this for <a href={...}> and <img src={...}> (img is dropped
 * via the allowlist anyway, but we defend-in-depth on the URL axis).
 */
export function safeUrlTransform(url: string): string | undefined {
  const lower = url.toLowerCase().trim();
  if (lower.startsWith("http://") || lower.startsWith("https://") || lower.startsWith("mailto:")) {
    return url;
  }
  return undefined; // dropping the URL renders the link text as plain text
}
